(() => {
  'use strict';
  if (customElements.get('bm-cavendish')) return;

  class BMCavendish extends HTMLElement {
    connectedCallback() {
      if (this.abortController) return;
      this.ambient = this.querySelector('[data-ambient]');
      this.film = this.querySelector('[data-film]');
      this.panel = this.querySelector('[data-film-panel]');
      if (!this.ambient || !this.film || !this.panel) return;
      this.abortController = new AbortController();
      this.operation = (this.operation || 0) + 1;
      this.active = false;
      this.visible = this.getBoundingClientRect().bottom > 0 && this.getBoundingClientRect().top < window.innerHeight;
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.mobile = matchMedia('(max-width: 749px)');
      this.connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
      this.ambientChoice = null;
      this.phoneReady = false;
      this.ambientCued = false;
      this.ambientRested = false;
      this.ambientWraps = 0;
      this.ambientLast = 0;
      this.frameWatch = false;
      this.ambientWanted = this.autoAmbient();
      this.homeContent = [...this.querySelectorAll('[data-home-content]')];
      this.ambientButton = this.querySelector('[data-ambient-toggle]');
      this.playButton = this.querySelector('[data-film-play]');
      this.soundButton = this.querySelector('[data-film-sound]');
      this.fullscreenButton = this.querySelector('[data-film-fullscreen]');
      this.progress = this.querySelector('[data-film-progress]');
      this.time = this.querySelector('[data-film-time]');
      this.status = this.querySelector('[data-film-status]');
      const on = (node, event, callback) => node?.addEventListener(event, callback, {signal: this.abortController.signal});
      this.querySelectorAll('[data-start-film]').forEach(button => on(button, 'click', () => this.start(button)));
      on(this.querySelector('[data-film-close]'), 'click', () => this.close());
      on(this.ambientButton, 'click', () => { this.ambientChoice = this.ambient.paused; this.ambientWanted = this.ambientChoice; this.syncAmbient(); });
      on(this.playButton, 'click', () => { if (this.film.paused) this.playFilm(); else this.film.pause(); });
      on(this.soundButton, 'click', () => {
        this.film.muted = !this.film.muted;
        if (!this.film.muted && this.film.volume === 0) this.film.volume = 1;
        this.syncFilm();
      });
      on(this.progress, 'input', () => {
        if (Number.isFinite(this.film.duration)) this.film.currentTime = Number(this.progress.value);
        this.syncProgress();
      });
      ['play', 'pause'].forEach(event => on(this.ambient, event, () => this.syncAmbientButton()));
      on(this.ambient, 'play', () => this.watchFrames());
      on(this.ambient, 'playing', () => this.revealAmbient());
      on(this.ambient, 'timeupdate', () => this.restAmbient());
      on(this.ambient, 'error', () => {
        this.ambientWanted = false;
        this.removeAttribute('data-ambient-loaded');
        // WebKit can leave a failed loop marked as playing; on phones the toggle must read Play over the still.
        if (this.mobile.matches) this.ambient.pause();
        this.syncAmbientButton();
      });
      ['play', 'pause', 'volumechange'].forEach(event => on(this.film, event, () => this.syncFilm()));
      ['timeupdate', 'loadedmetadata', 'durationchange'].forEach(event => on(this.film, event, () => this.syncProgress()));
      on(this.film, 'waiting', () => { if (this.active && !this.film.paused) this.message(this.label('loading')); });
      on(this.film, 'playing', () => this.message(''));
      on(this.film, 'ended', () => { this.message(''); this.syncFilm(); });
      on(this.film, 'error', () => { if (this.active) this.message(this.label('error')); });
      on(this, 'keydown', event => {
        if (this.active && event.key === 'Escape' && !document.fullscreenElement) {
          event.preventDefault();
          this.close();
        }
      });
      on(document, 'visibilitychange', () => {
        if (document.hidden && this.active) this.film.pause();
        this.syncAmbient();
      });
      const refreshAmbientPreference = () => {
        this.ambientWanted = this.ambientChoice ?? this.autoAmbient();
        this.syncAmbient();
      };
      on(this.motion, 'change', refreshAmbientPreference);
      on(this.connection, 'change', refreshAmbientPreference);
      on(this.mobile, 'change', refreshAmbientPreference);
      this.fullscreenButton.hidden = !this.panel.requestFullscreen;
      on(this.fullscreenButton, 'click', async () => {
        try {
          this.message('');
          if (document.fullscreenElement === this.panel) await document.exitFullscreen();
          else await this.panel.requestFullscreen();
        } catch { if (this.active) this.message(this.label('fullScreenError')); }
      });
      on(document, 'fullscreenchange', () => {
        this.fullscreenButton.textContent = this.label(document.fullscreenElement === this.panel ? 'exitFullScreen' : 'fullScreen');
      });
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(entries => {
          this.visible = entries[0].isIntersecting;
          if (!this.visible && this.active) this.film.pause();
          this.syncAmbient();
        }, {threshold: 0.08});
        this.observer.observe(this);
      }
      this.setAttribute('data-ready', '');
      this.syncFilm();
      this.syncAmbient();
      this.preparePhone();
    }

    // Desktop keeps its original rule. Phones also wait for the page load and then skip the loop
    // on a 2G connection, a reduced-data preference or a low battery that is not charging, and
    // stop it once it has come to rest.
    autoAmbient() {
      if (this.motion.matches || this.connection?.saveData) return false;
      return !this.mobile.matches || (this.phoneReady && !this.ambientRested);
    }

    // Phones: after three passes the loop comes to rest on the wide shot (3.5 s), the frame that
    // shows the whole machine. A visitor who pressed Play or Pause keeps their own choice.
    restAmbient() {
      const time = this.ambient.currentTime;
      if (time < this.ambientLast) this.ambientWraps++;
      this.ambientLast = time;
      this.skipToWide(time, this.frameWatch ? 1.3 : 1.04);
      if (!this.mobile.matches || this.ambientChoice !== null || this.ambientWraps < 3 || time < 3.5) return;
      this.ambientRested = true;
      this.ambientWanted = false;
      this.syncAmbient();
    }

    // Phones: from 1.32 s the portrait crop shows a lens flare of blank amber, then a soft crop of his back
    // with no machine in frame, until the wide shot opens on a keyframe at 2.84 s. Phones cut from the
    // bench close-up straight to the wide shot; desktop's wide frame keeps the whole loop. Each presented
    // frame is checked where the browser allows it; timeupdate checks too, from an earlier start where frames
    // are not watched, since it fires only every quarter second.
    skipToWide(time, from) {
      if (!this.mobile.matches || time < from || time >= 2.82 || this.ambient.seeking) return;
      this.ambient.currentTime = 2.85;
    }

    watchFrames() {
      const video = this.ambient;
      if (this.frameWatch || !this.mobile.matches || !('requestVideoFrameCallback' in video)) return;
      this.frameWatch = true;
      const signal = this.abortController.signal;
      const step = (now, frame) => {
        if (signal.aborted) return;
        if (video.paused || !this.mobile.matches) { this.frameWatch = false; return; }
        this.skipToWide(frame.mediaTime, 1.2);
        video.requestVideoFrameCallback(step);
      };
      video.requestVideoFrameCallback(step);
    }

    preparePhone() {
      const signal = this.abortController.signal;
      const decide = async () => {
        if (signal.aborted) return;
        let ready = !/2g$/.test(this.connection?.effectiveType || '') && !matchMedia('(prefers-reduced-data: reduce)').matches;
        if (ready && this.mobile.matches && navigator.getBattery) {
          try {
            const battery = await Promise.race([navigator.getBattery(), new Promise(resolve => setTimeout(resolve, 600))]);
            if (battery && !battery.charging && battery.level <= 0.2) ready = false;
          } catch {}
        }
        if (signal.aborted) return;
        this.phoneReady = ready;
        if (this.mobile.matches && this.ambientChoice === null) {
          this.ambientWanted = this.autoAmbient();
          this.syncAmbient();
        }
      };
      const idle = () => { if ('requestIdleCallback' in window) requestIdleCallback(decide, {timeout: 1500}); else setTimeout(decide, 250); };
      if (document.readyState === 'complete') idle();
      else window.addEventListener('load', idle, {once: true, signal});
    }

    // Phones: the poster is the wide shot that opens at 2.84 s of the loop, so the loop is revealed
    // only once it sits on that shot. The crossfade then shows no cut.
    revealAmbient() {
      const cue = 3;
      if (!this.mobile.matches || this.ambientCued) { this.setAttribute('data-ambient-loaded', ''); return; }
      this.ambientCued = true;
      const video = this.ambient;
      if (!(video.duration > cue + 1) || video.currentTime >= cue) { this.setAttribute('data-ambient-loaded', ''); return; }
      video.addEventListener('seeked', () => {
        if (this.isConnected) this.setAttribute('data-ambient-loaded', '');
      }, {once: true, signal: this.abortController.signal});
      video.currentTime = cue;
    }

    disconnectedCallback() {
      this.operation++;
      this.abortController?.abort();
      this.abortController = null;
      this.observer?.disconnect();
      this.ambient?.pause();
      this.film?.pause();
      if (this.film) this.film.muted = true;
      this.active = false;
      this.panel?.setAttribute('hidden', '');
      this.homeContent?.forEach(node => { node.inert = false; });
      this.removeAttribute('data-film-active');
      this.removeAttribute('data-ready');
    }

    label(key) {
      return this.dataset[`label${key[0].toUpperCase()}${key.slice(1)}`] || '';
    }

    syncAmbient() {
      if (!this.isConnected) return;
      const shouldPlay = this.ambientWanted && this.visible && !document.hidden && !this.active;
      if (!shouldPlay) { this.ambient.pause(); this.syncAmbientButton(); return; }
      const source = this.mobile.matches && this.dataset.ambientMobileUrl ? this.dataset.ambientMobileUrl : this.dataset.ambientUrl;
      if (!source) return;
      if (this.ambient.getAttribute('src') !== source) {
        this.removeAttribute('data-ambient-loaded');
        this.ambientCued = !this.mobile.matches;
        this.ambientWraps = 0;
        this.ambientLast = 0;
        this.ambient.src = source;
      }
      this.ambient.muted = true;
      this.ambient.play().catch(error => {
        if (error.name !== 'AbortError' && this.isConnected && !this.active) {
          this.ambientWanted = false;
          this.syncAmbientButton();
        }
      });
    }

    syncAmbientButton() {
      const playing = !this.ambient.paused;
      this.ambientButton.textContent = this.label(playing ? 'pause' : 'play');
      this.ambientButton.setAttribute('aria-label', this.label(playing ? 'pause' : 'play'));
      this.ambientButton.setAttribute('aria-pressed', String(playing));
    }

    start(opener) {
      if (!this.dataset.filmUrl) return;
      this.opener = opener;
      this.restoreScroll = {left: window.scrollX, top: window.scrollY};
      this.operation++;
      this.active = true;
      this.ambient.pause();
      this.panel.hidden = false;
      this.setAttribute('data-entered', '');
      this.setAttribute('data-film-active', '');
      this.homeContent.forEach(node => { node.inert = true; });
      this.scrollIntoView({block: 'start', behavior: 'instant'});
      if (this.film.getAttribute('src') !== this.dataset.filmUrl) this.film.src = this.dataset.filmUrl;
      this.film.currentTime = 0;
      this.film.muted = false;
      this.film.volume = 1;
      this.syncFilm();
      this.syncProgress();
      this.playFilm();
      this.querySelector('[data-film-close]').focus({preventScroll: true});
    }

    playFilm() {
      if (!this.active) return;
      if (this.film.error) { this.film.load(); this.film.currentTime = 0; }
      const operation = this.operation;
      this.message(this.label('loading'));
      this.film.play().then(() => {
        if (this.active && operation === this.operation && this.isConnected) this.message('');
      }).catch(error => {
        if (this.active && operation === this.operation && this.isConnected) {
          if (error.name !== 'AbortError') this.message(this.label('retry'));
          else this.message('');
          this.syncFilm();
        }
      });
    }

    close() {
      if (!this.active) return;
      this.operation++;
      this.film.pause();
      this.film.muted = true;
      this.active = false;
      if (document.fullscreenElement === this.panel) document.exitFullscreen().catch(() => {});
      this.panel.hidden = true;
      this.removeAttribute('data-film-active');
      this.homeContent.forEach(node => { node.inert = false; });
      this.message('');
      this.syncAmbient();
      if (this.restoreScroll) window.scrollTo({...this.restoreScroll, behavior: 'instant'});
      this.opener?.focus({preventScroll: true});
    }

    syncFilm() {
      this.playButton.textContent = this.label(this.film.paused ? 'play' : 'pause');
      this.playButton.setAttribute('aria-pressed', String(!this.film.paused));
      const audible = !this.film.muted && this.film.volume > 0;
      this.soundButton.textContent = this.label(audible ? 'soundOn' : 'soundOff');
      this.soundButton.setAttribute('aria-pressed', String(audible));
    }

    syncProgress() {
      const duration = Number.isFinite(this.film.duration) && this.film.duration > 0 ? this.film.duration : 73;
      const clock = value => `${Math.floor(value / 60)}:${String(Math.floor(value % 60)).padStart(2, '0')}`;
      this.time.textContent = `${clock(this.film.currentTime)} / ${clock(duration)}`;
      this.progress.disabled = !Number.isFinite(this.film.duration) || this.film.duration <= 0;
      this.progress.max = String(duration);
      this.progress.value = String(this.film.currentTime);
      this.progress.setAttribute('aria-valuetext', this.time.textContent);
    }

    message(text) {
      this.status.textContent = text;
      this.status.hidden = !text;
    }
  }
  customElements.define('bm-cavendish', BMCavendish);
})();
